/*
 * HRCPC Legal Diary — Mobile PWA App
 * Self-contained mobile experience for Advocates + Members.
 * Reuses ALL existing REST APIs on the same backend — no parallel data store.
 *
 * Route mount: /m/*  (see index.js — path prefix routing)
 * Screens:
 *   /m/                     — auth (email OTP) or role-detect first screen
 *   /m/advocate             — Advocate first screen: BCI ID card + menu
 *   /m/advocate/home        — Today's hearings + open tasks quick view
 *   /m/advocate/cases       — My Cases list
 *   /m/advocate/cases/:no   — Case detail (read view)
 *   /m/member               — Member first screen: photo + code + menu
 *   /m/member/cases         — My Cases (own only)
 *   /m/member/cases/:no     — Case detail + case updates (visible_to_client)
 *   /m/member/refer         — Referral card
 *
 * Auth: existing email OTP for admin/advocate, mobile OTP for member.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reuse the same axios token bridge as the main app
const LS_ADMIN = "hrcpc_admin_token";
const LS_CLIENT = "hrcpc_client_token";
function _tokenBridge() {
  axios.interceptors.request.handlers = axios.interceptors.request.handlers || [];
  axios.interceptors.request.use((cfg) => {
    const url = (cfg.url || "").toString();
    if (!url.startsWith(API)) return cfg;
    const admin = localStorage.getItem(LS_ADMIN);
    const client = localStorage.getItem(LS_CLIENT);
    if (admin) cfg.headers.Authorization = `Bearer ${admin}`;
    else if (client) cfg.headers.Authorization = `Bearer ${client}`;
    return cfg;
  });
  // Hard-revoke handler: if backend rejects our token (401) on any
  // authenticated endpoint, wipe local session and bounce to /m/. This is the
  // client-side twin of the server's one-device session binding.
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      try {
        const url = (err?.config?.url || "").toString();
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        const isMobile = window.location.pathname.startsWith("/m");
        const isAuthCall = url.includes("/api/auth/");
        if (isMobile && status === 401 && !isAuthCall) {
          // Only auto-logout if we actually had a token — avoids booting a
          // logged-out visitor who's just probing endpoints.
          const hadToken = !!(localStorage.getItem(LS_ADMIN) || localStorage.getItem(LS_CLIENT));
          if (hadToken) {
            localStorage.removeItem(LS_ADMIN);
            localStorage.removeItem(LS_CLIENT);
            localStorage.removeItem("hrcpc_role");
            localStorage.removeItem("hrcpc_user_email");
            localStorage.removeItem("hrcpc_is_super");
            localStorage.removeItem("hrcpc_member");
            try { toast.error(detail || "Signed out — you logged in on another device."); } catch (_) {}
            setTimeout(() => { window.location.href = "/m/"; }, 400);
          }
        }
      } catch (err) { console.error('[MobileApp.tokenBridge] 401 cleanup failed:', err); }
      return Promise.reject(err);
    }
  );
}
_tokenBridge();

// ---------- Router (simple pathname-based) ----------
function useMobileRoute() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const on = () => setPath(window.location.pathname);
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const navigate = (to) => {
    window.history.pushState({}, "", to);
    setPath(to);
  };
  return [path, navigate];
}

// ---------- Palette / small primitives ----------
const Palette = {
  navy: "#0a1740",
  navy2: "#111c53",
  saffron: "#f6a821",
  emerald: "#059669",
  ink: "#0f172a",
  paper: "#f8fafc",
  border: "#e2e8f0",
};

// ---------- Date helpers (DD-MMM-YYYY [Weekday]) ----------
const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const _WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
function fmtDate(iso) {
  if (!iso) return "";
  // Accept YYYY-MM-DD or ISO datetime. Parse strictly, no locale surprises.
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const y = +m[1], mo = +m[2] - 1, d = +m[3];
  const dt = new Date(Date.UTC(y, mo, d));
  const dow = _WEEKDAYS[dt.getUTCDay()];
  return `${String(d).padStart(2, "0")}-${_MONTHS[mo]}-${y} [${dow}]`;
}
function _todayISO() { return new Date().toISOString().slice(0, 10); }
function _daysUntil(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const a = new Date(Date.UTC(+m[1], +m[2]-1, +m[3]));
  const t = new Date(); const b = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()));
  return Math.round((a - b) / 86400000);
}
// Sort ascending by a YYYY-MM-DD key; missing dates go last.
function sortByDateAsc(items, key) {
  return [...(items || [])].sort((a, b) => {
    const av = a?.[key] || "9999-99-99";
    const bv = b?.[key] || "9999-99-99";
    return av < bv ? -1 : av > bv ? 1 : 0;
  });
}
// Sort tasks: overdue first, then upcoming nearest→farthest, undated last.
function sortTasksForBoard(items) {
  const today = _todayISO();
  const withKey = (items || []).map((t) => {
    const d = t?.due_date || "";
    let bucket = 2; // 0=overdue, 1=upcoming/undated-known, 2=undated
    if (d && d < today) bucket = 0;
    else if (d && d >= today) bucket = 1;
    return { t, bucket, key: d || "9999-99-99" };
  });
  withKey.sort((a, b) => a.bucket - b.bucket || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return withKey.map(({ t }) => t);
}

const AppFrame = ({ children, headerRight = null }) => (
  <div
    style={{
      minHeight: "100dvh",
      background: `linear-gradient(180deg, ${Palette.navy} 0%, ${Palette.navy2} 40%, #f5eee1 100%)`,
      color: Palette.ink,
      paddingTop: "env(safe-area-inset-top, 0)",
      paddingBottom: "env(safe-area-inset-bottom, 0)",
    }}
  >
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 16px 24px" }}>
      {children}
    </div>
    {headerRight}
  </div>
);

const Card = ({ children, style, ...p }) => (
  <div
    {...p}
    style={{
      background: "#fff",
      borderRadius: 18,
      boxShadow: "0 10px 40px -20px rgba(10,23,64,.35)",
      padding: 16,
      ...(style || {}),
    }}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, ...p }) => (
  <button
    {...p}
    style={{
      background: `linear-gradient(90deg, ${Palette.saffron}, #f38a1a)`,
      color: "#fff",
      border: "none",
      padding: "14px 18px",
      borderRadius: 14,
      fontWeight: 700,
      fontSize: 15,
      width: "100%",
      boxShadow: "0 8px 24px -12px rgba(246,168,33,.6)",
      cursor: "pointer",
      ...(p.style || {}),
    }}
  >
    {children}
  </button>
);

const GhostButton = ({ children, ...p }) => (
  <button
    {...p}
    style={{
      background: "transparent",
      border: `1px solid ${Palette.border}`,
      padding: "10px 14px",
      borderRadius: 12,
      fontWeight: 600,
      fontSize: 13,
      color: Palette.ink,
      cursor: "pointer",
      ...(p.style || {}),
    }}
  >
    {children}
  </button>
);

// ---------- Login (unified: email for admin/advocate, mobile for member) ----------
function LoginScreen({ navigate }) {
  const [mode, setMode] = useState("staff"); // 'staff' | 'member'
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    setBusy(true);
    try {
      if (mode === "staff") {
        if (!email.includes("@")) throw new Error("Please enter a valid email");
        // Try advocate first, then admin — both use the same shape
        try {
          await axios.post(`${API}/auth/advocate/otp-send`, { email });
        } catch (err) { console.debug("[MobileApp.otpSend] advocate path failed, trying admin:", err?.response?.status); }
        try {
          await axios.post(`${API}/auth/admin/otp-send`, { email });
        } catch (err) { console.debug("[MobileApp.otpSend] admin path failed:", err?.response?.status); }
        toast.success("If the email is registered, an OTP has been sent");
      } else {
        if (mobile.length !== 10) throw new Error("Enter a valid 10-digit mobile");
        await axios.post(`${API}/auth/otp-send`, { mobile });
        toast.success("If this mobile is registered, a code has been sent");
      }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      if (mode === "staff") {
        // Try advocate first, then admin
        let ok = false;
        try {
          const r = await axios.post(`${API}/auth/advocate/otp-verify`, { email, otp });
          if (r.data?.token) {
            localStorage.setItem(LS_ADMIN, r.data.token);
            localStorage.setItem("hrcpc_role", "advocate");
            localStorage.setItem("hrcpc_user_email", email);
            localStorage.setItem("hrcpc_is_super", r.data?.is_super ? "1" : "0");
            ok = true;
          }
        } catch (err) { console.debug("[MobileApp.otpVerify] advocate path failed, trying admin:", err?.response?.status); }
        if (!ok) {
          const r = await axios.post(`${API}/auth/admin/otp-verify`, { email, otp });
          if (r.data?.token) {
            localStorage.setItem(LS_ADMIN, r.data.token);
            localStorage.setItem("hrcpc_role", "advocate"); // treat admin as advocate-with-super in mobile
            localStorage.setItem("hrcpc_user_email", email);
            localStorage.setItem("hrcpc_is_super", "1");
            ok = true;
          }
        }
        if (!ok) throw new Error("Invalid OTP");
        navigate("/m/advocate");
      } else {
        const r = await axios.post(`${API}/auth/otp-login`, { mobile, otp });
        localStorage.setItem(LS_CLIENT, r.data.token);
        localStorage.setItem("hrcpc_role", "client");
        localStorage.setItem("hrcpc_member", JSON.stringify(r.data.client || {}));
        navigate("/m/member");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired OTP");
    } finally { setBusy(false); }
  };

  return (
    <AppFrame>
      <div style={{ padding: "24px 4px 12px", textAlign: "center" }}>
        <img src="/img/sudarshana-chakra.png" alt="Chakra" style={{ width: 92, height: 92, filter: "drop-shadow(0 0 24px rgba(246,168,33,.7))" }} />
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 22, marginTop: 12 }}>HRCPC Legal Diary</div>
        <div style={{ color: "rgba(255,255,255,.7)", fontSize: 12, marginTop: 4 }}>Human Rights & Consumer Protection Cell Trust</div>
      </div>

      <Card style={{ marginTop: 16 }} data-testid="mobile-login-card">
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => { setMode("staff"); setStep(1); }}
            data-testid="mobile-mode-staff"
            style={{ flex: 1, padding: "10px 12px", borderRadius: 12, border: `1px solid ${Palette.border}`,
              background: mode === "staff" ? Palette.navy : "#fff",
              color: mode === "staff" ? "#fff" : Palette.ink, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            Advocate / Admin
          </button>
          <button
            onClick={() => { setMode("member"); setStep(1); }}
            data-testid="mobile-mode-member"
            style={{ flex: 1, padding: "10px 12px", borderRadius: 12, border: `1px solid ${Palette.border}`,
              background: mode === "member" ? Palette.navy : "#fff",
              color: mode === "member" ? "#fff" : Palette.ink, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            Member
          </button>
        </div>
        {step === 1 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {mode === "staff" ? (
              <input data-testid="mobile-email-input" type="email" placeholder="your.name@hrcpc.org" value={email} onChange={(e) => setEmail(e.target.value)} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${Palette.border}`, fontSize: 15 }} />
            ) : (
              <input data-testid="mobile-mobile-input" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${Palette.border}`, fontSize: 15 }} />
            )}
            <PrimaryButton onClick={sendOtp} disabled={busy} data-testid="mobile-send-otp-btn">{busy ? "Sending…" : "Send OTP"}</PrimaryButton>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>A 6-digit code will arrive by {mode === "staff" ? "email" : "SMS / email"}. It expires in 5 minutes.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input data-testid="mobile-otp-input" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${Palette.border}`, fontSize: 22, letterSpacing: 8, textAlign: "center", fontFamily: "ui-monospace,monospace" }} />
            <PrimaryButton onClick={verifyOtp} disabled={busy} data-testid="mobile-verify-otp-btn">{busy ? "Verifying…" : "Verify & Enter"}</PrimaryButton>
            <GhostButton onClick={() => { setStep(1); setOtp(""); }}>Change {mode === "staff" ? "email" : "mobile"}</GhostButton>
          </div>
        )}
      </Card>
      <div style={{ textAlign: "center", color: "rgba(255,255,255,.7)", fontSize: 11, marginTop: 16 }}>
        Secure email/mobile OTP · One device active at a time · v1.0
      </div>
    </AppFrame>
  );
}

// ---------- Advocate first screen: BCI card + menu ----------
function AdvocateHome({ navigate }) {
  const email = localStorage.getItem("hrcpc_user_email") || "";
  const isSuper = localStorage.getItem("hrcpc_is_super") === "1";
  const [me, setMe] = useState(null);
  const [reload, setReload] = useState(0);
  const [showBci, setShowBci] = useState(false);
  useEffect(() => {
    axios.get(`${API}/advocate/profile`).then((r) => setMe(r.data)).catch(() => setMe({ email, name: email.split("@")[0] }));
  }, [email, reload]);

  const bciImg = me?.bci_id_image_url || null;
  const logout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem(LS_ADMIN);
    localStorage.removeItem("hrcpc_role");
    localStorage.removeItem("hrcpc_user_email");
    localStorage.removeItem("hrcpc_is_super");
    navigate("/m/");
  };

  return (
    <AppFrame>
      <div style={{ padding: "18px 4px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>HRCPC Legal Diary</div>
        <GhostButton onClick={logout} data-testid="mobile-logout" style={{ background: "rgba(255,255,255,.08)", color: "#fff", borderColor: "rgba(255,255,255,.15)" }}>Logout</GhostButton>
      </div>

      <InstallBanner />
      <NotifButton />

      {/* BCI Identity Card */}
      <Card style={{ padding: 0, overflow: "hidden", marginBottom: 16 }} data-testid="advocate-bci-card">
        <div style={{ background: `linear-gradient(135deg, ${Palette.navy}, #241f5a)`, color: "#fff", padding: "14px 16px" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", opacity: 0.75 }}>Bar Council of India · Panel Advocate</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>{me?.name || email.split("@")[0].replace(/\./g, " ")}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>{email}{isSuper ? " · Super Admin" : ""}</div>
          {(me?.bar_council_no || me?.enrolment_no) && (
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 6, fontFamily: "ui-monospace,monospace" }}>
              {me?.bar_council_no && <span>BCI: {me.bar_council_no}</span>}
              {me?.bar_council_no && me?.enrolment_no && <span> · </span>}
              {me?.enrolment_no && <span>Enrol: {me.enrolment_no}</span>}
            </div>
          )}
        </div>
        <div style={{ padding: 12, textAlign: "center", background: "#fffef5" }}>
          {bciImg ? (
            <img src={bciImg} alt="BCI Enrolment Card" style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 10, border: `1px solid ${Palette.border}`, background: "#fffef5" }} data-testid="advocate-bci-image" />
          ) : (
            <div style={{ padding: 24, background: "#fffbeb", border: `1px dashed ${Palette.saffron}`, borderRadius: 10, color: "#92400e", fontSize: 13, lineHeight: 1.5 }} data-testid="advocate-bci-placeholder">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>BCI enrolment image not uploaded yet</div>
              <div>Tap below to upload a photo of your enrolment card.</div>
            </div>
          )}
          <GhostButton onClick={() => setShowBci(true)} data-testid="btn-upload-bci" style={{ marginTop: 10, background: Palette.navy, color: "#fff", borderColor: Palette.navy }}>
            {bciImg ? "Replace / Edit BCI Details" : "Upload BCI Enrolment Card"}
          </GhostButton>
        </div>
      </Card>

      {/* Menu grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MenuTile label="Today" sub="Hearings & tasks" onClick={() => navigate("/m/advocate/home")} colour={Palette.emerald} testid="tile-today" />
        <MenuTile label="My Cases" sub="Active dossiers" onClick={() => navigate("/m/advocate/cases")} colour={Palette.saffron} testid="tile-cases" />
        <MenuTile label="My Tasks" sub="Overdue first" onClick={() => navigate("/m/advocate/tasks")} colour="#dc2626" testid="tile-tasks" />
        <MenuTile label="Recap Preview" sub="Friday digest" onClick={() => navigate("/m/advocate/recap")} colour="#0891b2" testid="tile-recap" />
      </div>

      {showBci && (
        <BciUploadSheet
          current={me}
          onClose={() => setShowBci(false)}
          onSaved={() => { setShowBci(false); setReload((n) => n + 1); }}
        />
      )}
    </AppFrame>
  );
}

// ---------- BCI Upload sheet ----------
function BciUploadSheet({ current, onClose, onSaved }) {
  const [barNo, setBarNo] = useState(current?.bar_council_no || "");
  const [enrolNo, setEnrolNo] = useState(current?.enrolment_no || "");
  const [preview, setPreview] = useState(current?.bci_id_image_url || null);
  const [busy, setBusy] = useState(false);

  const readFile = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read file"));
    r.readAsDataURL(f);
  });
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Please choose an image file"); return; }
    if (f.size > 3_000_000) { toast.error("Image is larger than 3 MB. Please compress and try again."); return; }
    try { const dataUrl = await readFile(f); setPreview(dataUrl); }
    catch (_) { toast.error("Could not read the image"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { bar_council_no: barNo.trim(), enrolment_no: enrolNo.trim() };
      if (preview && preview !== current?.bci_id_image_url) body.bci_id_image_url = preview;
      await axios.patch(`${API}/advocate/profile`, body);
      toast.success("BCI details saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  const removeImg = async () => {
    if (!window.confirm("Remove the BCI enrolment image?")) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/advocate/profile`, { bci_id_image_url: null });
      toast.success("Image removed");
      onSaved();
    } catch (e) {
      toast.error("Could not remove");
    } finally { setBusy(false); }
  };

  return (
    <Sheet onClose={onClose} title="BCI Enrolment Card">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Bar Council No.
          <input value={barNo} onChange={(e) => setBarNo(e.target.value)} placeholder="e.g. AP/12345/2020" data-testid="bci-bar-no" style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%" }} />
        </label>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Enrolment No.
          <input value={enrolNo} onChange={(e) => setEnrolNo(e.target.value)} placeholder="e.g. E-99A / 2015" data-testid="bci-enrol-no" style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%" }} />
        </label>
        <div>
          <div style={{ fontSize: 12, color: "#475569", fontWeight: 600, marginBottom: 6 }}>Enrolment card image (max 3 MB)</div>
          {preview ? (
            <div style={{ textAlign: "center" }}>
              <img src={preview} alt="BCI preview" data-testid="bci-preview" style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 10, border: `1px solid ${Palette.border}`, background: "#fffef5" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <label style={{ flex: 1 }}>
                  <input type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} data-testid="bci-file-replace" />
                  <div style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, textAlign: "center", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Replace</div>
                </label>
                {current?.bci_id_image_url && (
                  <GhostButton onClick={removeImg} data-testid="bci-remove" style={{ flex: 1, color: "#b91c1c" }}>Remove</GhostButton>
                )}
              </div>
            </div>
          ) : (
            <label style={{ display: "block" }}>
              <input type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} data-testid="bci-file-pick" />
              <div style={{ padding: 24, textAlign: "center", background: "#fffbeb", border: `2px dashed ${Palette.saffron}`, borderRadius: 12, cursor: "pointer", color: "#92400e", fontWeight: 600, fontSize: 13 }}>Tap to choose image</div>
            </label>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <GhostButton onClick={onClose} style={{ flex: 1 }}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={busy} data-testid="bci-save-btn" style={{ flex: 2 }}>{busy ? "Saving…" : "Save"}</PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
}

// ---------- PWA Install Banner ----------
const INSTALL_DISMISS_KEY = "hrcpc_install_dismissed_at";
const INSTALL_HIDE_DAYS = 7;

// ---------- Push Notification button ----------
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function NotifButton() {
  const [supported] = useState(() => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window);
  const [state, setState] = useState("checking"); // 'unsupported' | 'blocked' | 'idle' | 'subscribed' | 'busy'
  const [endpoint, setEndpoint] = useState(null);

  useEffect(() => {
    (async () => {
      if (!supported) return setState("unsupported");
      if (Notification.permission === "denied") return setState("blocked");
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) { setEndpoint(sub.endpoint); setState("subscribed"); }
        else setState("idle");
      } catch (_) { setState("idle"); }
    })();
  }, [supported]);

  const enable = async () => {
    setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "blocked" : "idle"); return; }
      const r = await axios.get(`${API}/push/vapid-public-key`);
      const key = r.data?.public_key;
      if (!key) throw new Error("Push not configured on server");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
      setEndpoint(sub.endpoint);
      setState("subscribed");
      toast.success("Notifications enabled");
      // Fire a test push so the user sees the flow working end-to-end.
      try { await axios.post(`${API}/push/test`); } catch (err) { console.error('[PushSettings.enable] test push failed:', err); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not enable notifications");
      setState("idle");
    }
  };

  const disable = async () => {
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const ep = sub?.endpoint || endpoint;
      if (sub) await sub.unsubscribe().catch(() => {});
      await axios.post(`${API}/push/unsubscribe`, { endpoint: ep });
      setEndpoint(null);
      setState("idle");
      toast.success("Notifications turned off");
    } catch (e) {
      toast.error("Could not turn off notifications");
      setState("subscribed");
    }
  };

  if (state === "unsupported") return null;
  if (state === "blocked") {
    return (
      <div data-testid="notif-blocked" style={{ marginBottom: 12, padding: 10, borderRadius: 12, background: "#fee2e2", border: "1px solid #fca5a5", fontSize: 12, color: "#991b1b" }}>
        Notifications are blocked. Open browser settings to allow them for this site.
      </div>
    );
  }
  const isSubbed = state === "subscribed";
  // UX FIX (Feb 14, 2026): once notifications are ON, do NOT hog the top of the
  // home screen with a full-width green card. Render a compact one-liner chip
  // with a small mute button so the identity card + menu grid regain the space.
  if (isSubbed) {
    return (
      <div data-testid="notif-chip" style={{ marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
        <span style={{ fontSize: 11, color: "#065f46", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#059669", display: "inline-block" }} />
          Notifications on
        </span>
        <button onClick={disable} disabled={state === "busy"} data-testid="notif-disable"
          style={{ padding: "3px 8px", background: "transparent", border: "1px solid #cbd5e1", color: "#475569", borderRadius: 8, fontWeight: 600, fontSize: 10, cursor: "pointer" }}>
          Turn off
        </button>
      </div>
    );
  }
  return (
    <div data-testid="notif-card" style={{ marginBottom: 12, padding: 10, borderRadius: 12, background: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔕</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Turn on notifications</div>
        <div style={{ fontSize: 11, color: "#334155", marginTop: 2 }}>Get hearing reminders and case updates on this phone.</div>
      </div>
      <button onClick={enable} disabled={state === "busy"} data-testid="notif-enable"
        style={{ padding: "8px 14px", background: Palette.saffron, color: "#fff", border: 0, borderRadius: 10, fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
        {state === "busy" ? "…" : "Enable"}
      </button>
    </div>
  );
}

function InstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    // Already installed?
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;
    // Recently dismissed?
    const dismissedAt = parseInt(localStorage.getItem(INSTALL_DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < INSTALL_HIDE_DAYS * 24 * 3600 * 1000) return;

    // iOS Safari path — no beforeinstallprompt available; show hint instead.
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      setIosMode(true);
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setIosOpen(false);
  };

  const install = async () => {
    if (iosMode) { setIosOpen(true); return; }
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        toast.success("Installed! Launch from your home screen.");
      }
    } catch (err) { console.error('[PWA install] prompt failed:', err); }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div data-testid="pwa-install-banner" style={{ marginBottom: 12, padding: 14, borderRadius: 16, background: "linear-gradient(120deg, #fffef5, #fef3c7)", border: `1px solid ${Palette.saffron}`, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 24px -18px rgba(246,168,33,.6)" }}>
      <div style={{ width: 44, height: 44, flex: "0 0 44px", borderRadius: 12, background: Palette.navy, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <img src="/img/icon-192.png" alt="HRCPC" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Install HRCPC Legal Diary</div>
        <div style={{ fontSize: 11, color: "#78350f", marginTop: 2 }}>Add to home screen — open with one tap, works offline.</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <button onClick={install} data-testid="pwa-install-btn" style={{ background: Palette.saffron, color: "#fff", border: 0, padding: "8px 14px", borderRadius: 10, fontWeight: 800, fontSize: 12, cursor: "pointer" }}>{iosMode ? "How?" : "Install"}</button>
        <button onClick={dismiss} data-testid="pwa-install-dismiss" style={{ background: "transparent", border: 0, color: "#78350f", fontSize: 11, cursor: "pointer", padding: "2px 8px", fontWeight: 700 }}>Later</button>
      </div>
      {iosMode && iosOpen && (
        <Sheet onClose={() => setIosOpen(false)} title="Install on iPhone / iPad">
          <ol style={{ paddingLeft: 20, fontSize: 14, lineHeight: 1.7, color: Palette.ink }}>
            <li>Tap the <b>Share</b> button (square with an up-arrow) at the bottom of Safari.</li>
            <li>Scroll and tap <b>Add to Home Screen</b>.</li>
            <li>Tap <b>Add</b> in the top right.</li>
          </ol>
          <div style={{ padding: 12, background: "#f8fafc", borderRadius: 10, fontSize: 12, color: "#475569" }}>Once added, tap the HRCPC icon on your home screen to launch the diary just like a native app.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <GhostButton onClick={() => setIosOpen(false)} style={{ flex: 1 }}>Close</GhostButton>
            <PrimaryButton onClick={dismiss} style={{ flex: 2 }} data-testid="pwa-ios-got-it">Got it</PrimaryButton>
          </div>
        </Sheet>
      )}
    </div>
  );
}

const MenuTile = ({ label, sub, onClick, colour, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    style={{
      background: "#fff",
      border: `1px solid ${Palette.border}`,
      borderRadius: 16,
      padding: "16px 14px",
      textAlign: "left",
      cursor: "pointer",
      boxShadow: "0 6px 20px -14px rgba(10,23,64,.4)",
    }}
  >
    <div style={{ width: 32, height: 4, borderRadius: 4, background: colour, marginBottom: 10 }} />
    <div style={{ fontWeight: 800, fontSize: 15, color: Palette.ink }}>{label}</div>
    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{sub}</div>
  </button>
);

// ---------- Advocate: Today ----------
function AdvocateToday({ navigate }) {
  const email = localStorage.getItem("hrcpc_user_email") || "";
  const [data, setData] = useState(null);
  useEffect(() => {
    // Uses the existing self-preview endpoint we added earlier
    axios.get(`${API}/advocate/my-recap-preview`)
      .then((r) => setData(r.data?.digest || null))
      .catch(() => setData(null));
  }, []);
  return (
    <AppFrame>
      <TopBar title="Today" onBack={() => navigate("/m/advocate")} />
      {!data ? <Skeleton /> : (
        <>
          <Section title="Upcoming hearings (next 7 days)">
            <List items={sortByDateAsc(data.upcoming_hearings || [], "date")} render={(h) => (
              <div>
                <div style={{ fontWeight: 700, color: Palette.ink }}>{h.title || "—"}</div>
                <div style={{ fontSize: 12, color: "#475569" }}>{h.case_number} · {h.court || h.forum_type || ""}</div>
                <div style={{ fontSize: 12, color: Palette.emerald, fontWeight: 700, marginTop: 4 }}>📅 {fmtDate(h.date)}</div>
              </div>
            )} emptyText="No hearings in the next 7 days." />
          </Section>
          <Section title={`Open tasks (${data.total_open_tasks || 0})`}>
            {(data.tasks_overdue || []).length > 0 && (
              <TaskBucket label="🔴 Overdue" items={sortByDateAsc(data.tasks_overdue, "due_date")} />
            )}
            <TaskBucket label="🟦 Due this week" items={sortByDateAsc(data.tasks_this_week || [], "due_date")} />
            <TaskBucket label="⚪ Upcoming" items={sortByDateAsc(data.tasks_upcoming || [], "due_date")} />
          </Section>
        </>
      )}
    </AppFrame>
  );
}

const TaskBucket = ({ label, items }) => (
  <div style={{ marginTop: 10 }}>
    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>{label} ({items.length})</div>
    <List items={items.slice(0, 8)} render={(t) => (
      <div>
        <div style={{ fontWeight: 700 }}>{t.title || "—"}</div>
        <div style={{ fontSize: 12, color: "#475569" }}>{t.case_number} · {t.task_type} · <span style={{ color: "#b91c1c", fontWeight: 700 }}>{fmtDate(t.due_date)}</span></div>
      </div>
    )} emptyText="Nothing here." />
  </div>
);

// ---------- Advocate: Cases ----------
function AdvocateCases({ navigate }) {
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { axios.get(`${API}/cases`).then((r) => setCases(r.data || [])).catch(() => setCases([])); }, []);
  const list = sortByDateAsc((cases || []).filter((c) => !q || (c.title || "").toLowerCase().includes(q.toLowerCase()) || (c.case_number || "").toLowerCase().includes(q.toLowerCase()) || (c.client_name || "").toLowerCase().includes(q.toLowerCase())), "next_hearing_date");
  return (
    <AppFrame>
      <TopBar title={`My Cases${cases ? ` (${cases.length})` : ""}`} onBack={() => navigate("/m/advocate")} />
      <input placeholder="Search title, case #, member…" value={q} onChange={(e) => setQ(e.target.value)}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 12, border: `1px solid ${Palette.border}`, marginBottom: 10, fontSize: 14 }} data-testid="advocate-case-search" />
      {!cases ? <Skeleton /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.length === 0 && <Card><em style={{ color: "#64748b" }}>No cases found.</em></Card>}
          {list.map((c) => (
            <Card key={c.case_number} data-testid={`case-card-${c.case_number}`} onClick={() => navigate(`/m/advocate/cases/${encodeURIComponent(c.case_number)}`)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, color: Palette.ink }}>{c.title || "—"}</div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{c.case_number} · {c.court || c.forum_type}</div>
                  <div style={{ fontSize: 12, color: "#475569" }}>Member: {c.client_name} · +91 {c.client_mobile}</div>
                </div>
                <StatusChip status={c.status} />
              </div>
              {c.next_hearing_date && (
                <div style={{ marginTop: 10, fontSize: 12, background: "#fef3c7", color: "#92400e", padding: "6px 10px", borderRadius: 8, fontWeight: 600, display: "inline-block" }}>📅 Next: {fmtDate(c.next_hearing_date)}</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </AppFrame>
  );
}

// ---------- Advocate: Case Detail (with Session-2 write actions) ----------
function AdvocateCaseDetail({ navigate, caseNumber }) {
  const [detail, setDetail] = useState(null);
  const [proceedings, setProceedings] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [parties, setParties] = useState(null);
  const [showHearing, setShowHearing] = useState(false);
  const [showProceed, setShowProceed] = useState(false);
  const [reload, setReload] = useState(0);
  const [togglingDocId, setTogglingDocId] = useState(null);
  useEffect(() => {
    // Fetch single-case detail (includes documents with visibility flag)
    axios.get(`${API}/cases/${encodeURIComponent(caseNumber)}`).then((r) => setDetail(r.data || null))
      .catch(() => axios.get(`${API}/cases`).then((r) => setDetail((r.data || []).find((c) => c.case_number === caseNumber) || null)).catch(() => setDetail(null)));
    axios.get(`${API}/case-proceedings?case_number=${encodeURIComponent(caseNumber)}`).then((r) => setProceedings(r.data || [])).catch(() => setProceedings([]));
    axios.get(`${API}/tasks?case_number=${encodeURIComponent(caseNumber)}`).then((r) => setTasks(r.data || [])).catch(() => setTasks([]));
    // Feb 14, 2026 — parties & counsels list (read-only for mobile)
    axios.get(`${API}/case-parties?case_number=${encodeURIComponent(caseNumber)}`).then((r) => setParties(r.data || [])).catch(() => setParties([]));
  }, [caseNumber, reload]);

  const completeTask = async (t) => {
    const note = window.prompt(`Mark task "${t.title}" as complete?\nOptional note:`, "");
    if (note === null) return;
    try {
      await axios.patch(`${API}/tasks/${t.id}`, { status: "Completed", completion_notes: note || "Completed via mobile app" });
      toast.success("Task marked complete");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete task");
    }
  };

  const toggleDocVisibility = async (doc) => {
    const next = !doc.visible_to_client;
    setTogglingDocId(doc.id);
    // Optimistic UI
    setDetail((prev) => prev ? {
      ...prev,
      documents: (prev.documents || []).map((d) => d.id === doc.id ? { ...d, visible_to_client: next } : d)
    } : prev);
    try {
      await axios.patch(`${API}/cases/${encodeURIComponent(caseNumber)}/documents/${encodeURIComponent(doc.id)}`, { visible_to_client: next });
      toast.success(next ? "Now visible to Member" : "Hidden from Member");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update visibility");
      // Revert
      setDetail((prev) => prev ? {
        ...prev,
        documents: (prev.documents || []).map((d) => d.id === doc.id ? { ...d, visible_to_client: !next } : d)
      } : prev);
    } finally { setTogglingDocId(null); }
  };

  const documents = (detail?.documents || []);
  const sortedProceedings = [...(proceedings || [])].sort((a, b) => (a.event_date || "") < (b.event_date || "") ? 1 : -1); // newest first
  const sortedTasks = sortTasksForBoard(tasks || []);

  return (
    <AppFrame>
      <TopBar title="Case" onBack={() => navigate("/m/advocate/cases")} />
      {!detail ? <Skeleton /> : (
        <>
          <Card>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{detail.title}</div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>{detail.case_number} · {detail.court || detail.forum_type}</div>
            <div style={{ fontSize: 13, color: "#475569" }}>Member: <b>{detail.client_name}</b></div>
            <div style={{ marginTop: 8 }}><StatusChip status={detail.status} /></div>
            {detail.next_hearing_date && <div style={{ marginTop: 10, fontSize: 13, background: "#fef3c7", color: "#92400e", padding: "8px 12px", borderRadius: 8, fontWeight: 700 }} data-testid="case-next-hearing">📅 Next hearing: {fmtDate(detail.next_hearing_date)}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <GhostButton onClick={() => setShowHearing(true)} data-testid="btn-open-hearing" style={{ flex: 1, minWidth: 140, background: "#0f172a", color: "#fff", borderColor: "#0f172a" }}>📅 Update next hearing</GhostButton>
              <GhostButton onClick={() => setShowProceed(true)} data-testid="btn-open-proceeding" style={{ flex: 1, minWidth: 140, background: Palette.emerald, color: "#fff", borderColor: Palette.emerald }}>➕ Add proceeding</GhostButton>
            </div>
          </Card>

          <Section title={`Documents (${documents.length})`}>
            {documents.length === 0 ? (
              <Card><em style={{ color: "#64748b" }}>No documents uploaded yet. Upload from the Admin Panel for now — mobile upload coming soon.</em></Card>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {documents.map((d) => (
                  <Card key={d.id} data-testid={`advocate-doc-${d.id}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fef3c7", color: "#92400e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flex: "0 0 36px" }}>📄</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: Palette.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{d.type} · {fmtDate(d.date)}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 10, background: d.visible_to_client ? "#dcfce7" : "#f1f5f9" }} data-testid={`doc-visibility-row-${d.id}`}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: d.visible_to_client ? "#065f46" : "#475569" }}>
                        {d.visible_to_client ? "Visible to Member" : "Internal only"}
                      </div>
                      <button
                        onClick={() => toggleDocVisibility(d)}
                        disabled={togglingDocId === d.id}
                        data-testid={`doc-visibility-toggle-${d.id}`}
                        aria-label="Toggle visibility to member"
                        style={{
                          position: "relative",
                          width: 44, height: 24, borderRadius: 999,
                          background: d.visible_to_client ? Palette.emerald : "#cbd5e1",
                          border: 0, cursor: togglingDocId === d.id ? "wait" : "pointer",
                          transition: "background .2s",
                        }}
                      >
                        <span style={{
                          position: "absolute", top: 2, left: d.visible_to_client ? 22 : 2,
                          width: 20, height: 20, borderRadius: "50%",
                          background: "#fff", transition: "left .2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,.25)",
                        }} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Section>

          <Section title="Proceedings / Timeline">
            <List items={sortedProceedings} render={(p) => (
              <div>
                <div style={{ fontSize: 12, color: "#0f766e", fontWeight: 700 }}>{fmtDate(p.event_date)} · {p.event_type}</div>
                <div style={{ fontWeight: 700, marginTop: 2 }}>{p.details || p.summary || "—"}</div>
                {p.next_step && <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Next: {p.next_step} {p.next_step_due && `by ${fmtDate(p.next_step_due)}`}</div>}
                <div style={{ marginTop: 6 }}>
                  {p.visible_to_client ? <span style={{ background: "#dcfce7", color: "#065f46", fontSize: 10, padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>Visible to Member</span> : <span style={{ background: "#f1f5f9", color: "#475569", fontSize: 10, padding: "2px 6px", borderRadius: 999, fontWeight: 700 }}>Internal</span>}
                </div>
              </div>
            )} emptyText="No proceedings yet. Tap ‘Add proceeding’ to publish an update." />
          </Section>

          <Section title={`Parties & Counsels (${(parties || []).length})`}>
            {/* Feb 14, 2026 — read-only parties/counsel list for advocates on mobile.
                Members are denied at the API — never rendered on member view. */}
            <List items={parties || []} render={(p) => {
              const sideLabel = p.party_side === "Applicant-Petitioner-Complainant" ? "Petitioner / Complainant"
                : p.party_side === "Respondent-Defendant-Accused" ? "Respondent / Defendant"
                : (p.party_side || "");
              const sideColor = p.party_side?.startsWith("Applicant") ? "#065f46"
                : p.party_side?.startsWith("Respondent") ? "#9f1239" : "#475569";
              const sideBg = p.party_side?.startsWith("Applicant") ? "#dcfce7"
                : p.party_side?.startsWith("Respondent") ? "#fee2e2" : "#f1f5f9";
              return (
                <div data-testid={`m-party-${p.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: sideBg, color: sideColor }}>{sideLabel}</span>
                    <span style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase" }}>{p.party_kind}</span>
                    {p.is_primary_contact && <span style={{ fontSize: 10, fontWeight: 800, color: "#92400e", background: "#fef3c7", padding: "2px 6px", borderRadius: 4 }}>★ Primary</span>}
                  </div>
                  <div style={{ fontWeight: 700, marginTop: 4 }}>{p.party_name}</div>
                  {(p.party_mobile || p.party_email) && (
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {p.party_mobile && <a href={`tel:${p.party_mobile}`} style={{ color: "#0f766e", fontWeight: 600, marginRight: 8 }}>📞 {p.party_mobile}</a>}
                      {p.party_email && <a href={`mailto:${p.party_email}`} style={{ color: "#0f766e", fontWeight: 600 }}>✉ {p.party_email}</a>}
                    </div>
                  )}
                  {p.counsel_name && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #e2e8f0" }}>
                      <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#475569" }}>Counsel</div>
                      <div style={{ fontWeight: 700 }}>{p.counsel_name}</div>
                      {(p.counsel_mobile || p.counsel_email) && (
                        <div style={{ fontSize: 12, marginTop: 2 }}>
                          {p.counsel_mobile && <a href={`tel:${p.counsel_mobile}`} style={{ color: "#0f766e", fontWeight: 600, marginRight: 8 }}>📞 {p.counsel_mobile}</a>}
                          {p.counsel_email && <a href={`mailto:${p.counsel_email}`} style={{ color: "#0f766e", fontWeight: 600 }}>✉ {p.counsel_email}</a>}
                        </div>
                      )}
                      {p.counsel_for_note && <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 2 }}>{p.counsel_for_note}</div>}
                    </div>
                  )}
                </div>
              );
            }} emptyText="No parties recorded. Add parties from the desktop case view." />
          </Section>

          <Section title={`Tasks (${sortedTasks.length})`}>
            <List items={sortedTasks} render={(t) => {
              const days = _daysUntil(t.due_date);
              const isOverdue = t.status !== "Completed" && days !== null && days < 0;
              return (
                <div>
                  <div style={{ fontWeight: 700 }}>{t.title}</div>
                  <div style={{ fontSize: 12, color: "#475569" }}>
                    {t.task_type} · {t.priority} · Due {t.due_date ? fmtDate(t.due_date) : "—"} · <b style={{ color: t.status === "Completed" ? Palette.emerald : "#b45309" }}>{t.status}</b>
                    {isOverdue && <span style={{ marginLeft: 6, background: "#fee2e2", color: "#991b1b", fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 999 }}>OVERDUE</span>}
                  </div>
                  {t.status !== "Completed" && (
                    <GhostButton onClick={() => completeTask(t)} data-testid={`complete-task-${t.id}`} style={{ marginTop: 8, background: Palette.emerald, color: "#fff", borderColor: Palette.emerald }}>✓ Mark complete</GhostButton>
                  )}
                  {t.completion_notes && <div style={{ marginTop: 6, fontSize: 12, color: "#065f46", background: "#dcfce7", padding: "6px 10px", borderRadius: 8 }}>“{t.completion_notes}”</div>}
                </div>
              );
            }} emptyText="No tasks yet." />
          </Section>
        </>
      )}

      {showHearing && (
        <NextHearingSheet
          currentDate={detail?.next_hearing_date || ""}
          caseNumber={caseNumber}
          onClose={() => setShowHearing(false)}
          onSaved={() => { setShowHearing(false); setReload((n) => n + 1); }}
        />
      )}
      {showProceed && (
        <AddProceedingSheet
          caseNumber={caseNumber}
          onClose={() => setShowProceed(false)}
          onSaved={() => { setShowProceed(false); setReload((n) => n + 1); }}
        />
      )}
    </AppFrame>
  );
}

// ---------- Bottom sheet: Update next hearing date ----------
function NextHearingSheet({ currentDate, caseNumber, onClose, onSaved }) {
  const [date, setDate] = useState(currentDate || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { toast.error("Please pick a date (YYYY-MM-DD)"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/cases/${encodeURIComponent(caseNumber)}/status`, { next_hearing_date: date });
      toast.success("Next hearing updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update hearing date");
    } finally { setBusy(false); }
  };
  return (
    <Sheet onClose={onClose} title="Update next hearing">
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Case {caseNumber}</div>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ padding: "12px 14px", borderRadius: 12, border: `1px solid ${Palette.border}`, fontSize: 15, width: "100%" }} data-testid="hearing-date-input" />
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <GhostButton onClick={onClose} style={{ flex: 1 }}>Cancel</GhostButton>
        <PrimaryButton onClick={save} disabled={busy} data-testid="hearing-save-btn" style={{ flex: 2 }}>{busy ? "Saving…" : "Save date"}</PrimaryButton>
      </div>
    </Sheet>
  );
}

// ---------- Bottom sheet: Add proceeding ----------
const EVENT_TYPES = [
  "Filing", "Notice Issued", "Notice Served", "Reply Filed", "Hearing",
  "Adjournment", "Interim Order", "Evidence", "Arguments", "Judgment",
  "Order", "Appeal", "Settlement", "Compliance", "Follow-up",
  "Correspondence", "Meeting", "Other",
];
function AddProceedingSheet({ caseNumber, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [eventType, setEventType] = useState("Hearing");
  const [eventDate, setEventDate] = useState(today);
  const [details, setDetails] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [nextStepDue, setNextStepDue] = useState("");
  const [visibleToMember, setVisibleToMember] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!details.trim()) { toast.error("Please enter proceeding details"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/case-proceedings`, {
        case_number: caseNumber,
        event_type: eventType,
        event_date: eventDate,
        details: details.trim(),
        next_step: nextStep.trim() || null,
        next_step_due: nextStepDue || null,
        visible_to_client: visibleToMember,
      });
      toast.success(visibleToMember ? "Proceeding added & shared with Member" : "Proceeding added (internal only)");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add proceeding");
    } finally { setBusy(false); }
  };

  return (
    <Sheet onClose={onClose} title="Add proceeding">
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Case {caseNumber}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Event type
          <select value={eventType} onChange={(e) => setEventType(e.target.value)} style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%", background: "#fff" }} data-testid="proc-event-type">
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Event date
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%" }} data-testid="proc-event-date" />
        </label>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Details
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What happened today — for the Member if visible, or for the case file if internal." rows={4} style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%", resize: "vertical" }} data-testid="proc-details" />
        </label>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Next step (optional)
          <input value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="e.g. File written statement" style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%" }} data-testid="proc-next-step" />
        </label>
        <label style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Due (optional)
          <input type="date" value={nextStepDue} onChange={(e) => setNextStepDue(e.target.value)} style={{ marginTop: 4, padding: "10px 12px", borderRadius: 10, border: `1px solid ${Palette.border}`, fontSize: 14, width: "100%" }} data-testid="proc-next-due" />
        </label>

        <div style={{ marginTop: 4, padding: 12, background: visibleToMember ? "#dcfce7" : "#f1f5f9", borderRadius: 12, border: `1px solid ${visibleToMember ? "#86efac" : Palette.border}` }} data-testid="proc-visibility-block">
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={visibleToMember} onChange={(e) => setVisibleToMember(e.target.checked)} data-testid="proc-visibility-toggle" style={{ width: 20, height: 20 }} />
            <div>
              <div style={{ fontWeight: 700, color: visibleToMember ? "#065f46" : "#475569", fontSize: 14 }}>{visibleToMember ? "Visible to Member" : "Internal only"}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{visibleToMember ? "The Member will see this update on their mobile app." : "Kept in the case file — the Member will NOT see this."}</div>
            </div>
          </label>
        </div>
        {visibleToMember && (
          <div style={{ marginTop: -4, padding: 10, background: "#fffbeb", border: `1px dashed ${Palette.saffron}`, borderRadius: 10, fontSize: 12, color: "#92400e" }} data-testid="proc-member-preview">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Preview shown to Member:</div>
            <div><b>{fmtDate(eventDate)}</b> · {eventType}</div>
            <div style={{ marginTop: 2 }}>{details || <em>Enter details above</em>}</div>
            {nextStep && <div style={{ marginTop: 2 }}>Next: {nextStep}{nextStepDue && ` by ${fmtDate(nextStepDue)}`}</div>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <GhostButton onClick={onClose} style={{ flex: 1 }}>Cancel</GhostButton>
          <PrimaryButton onClick={save} disabled={busy} data-testid="proc-save-btn" style={{ flex: 2 }}>{busy ? "Saving…" : "Save proceeding"}</PrimaryButton>
        </div>
      </div>
    </Sheet>
  );
}

// ---------- Bottom sheet primitive ----------
const Sheet = ({ children, onClose, title }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, background: "#fff", borderRadius: "18px 18px 0 0", padding: "16px 18px 22px", boxShadow: "0 -12px 40px rgba(0,0,0,.35)", maxHeight: "90dvh", overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>{title}</div>
        <button onClick={onClose} data-testid="sheet-close" style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748b", lineHeight: 1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// ---------- Advocate: Tasks (mirrors My Tasks web page — with complete action) ----------
function AdvocateTasks({ navigate }) {
  const [tasks, setTasks] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { axios.get(`${API}/my-tasks`).then((r) => setTasks(r.data?.mine || r.data || [])).catch(() => setTasks([])); }, [reload]);
  const complete = async (t) => {
    const note = window.prompt(`Mark "${t.title}" complete?\nOptional note:`, "");
    if (note === null) return;
    try {
      await axios.patch(`${API}/tasks/${t.id}`, { status: "Completed", completion_notes: note || "Completed via mobile app" });
      toast.success("Task marked complete");
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete task");
    }
  };
  const sortedTasks = sortTasksForBoard(tasks || []);
  return (
    <AppFrame>
      <TopBar title="My Tasks" onBack={() => navigate("/m/advocate")} />
      {!tasks ? <Skeleton /> : (
        <List items={sortedTasks} render={(t) => {
          const days = _daysUntil(t.due_date);
          const isOverdue = t.status !== "Completed" && days !== null && days < 0;
          return (
            <div>
              <div style={{ fontWeight: 700 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{t.task_type} · Priority: {t.priority}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Due: <b style={{ color: isOverdue ? "#b91c1c" : Palette.ink }}>{t.due_date ? fmtDate(t.due_date) : "—"}</b> · Status: <b style={{ color: t.status === "Completed" ? Palette.emerald : "#b45309" }}>{t.status}</b>
                {isOverdue && <span style={{ marginLeft: 6, background: "#fee2e2", color: "#991b1b", fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 999 }}>OVERDUE</span>}
              </div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Case: {t.case_number}</div>
              {t.status !== "Completed" && (
                <GhostButton onClick={() => complete(t)} data-testid={`complete-task-${t.id}`} style={{ marginTop: 8, background: Palette.emerald, color: "#fff", borderColor: Palette.emerald }}>✓ Mark complete</GhostButton>
              )}
            </div>
          );
        }} emptyText="No tasks assigned to you." />
      )}
    </AppFrame>
  );
}

// ---------- Advocate: Recap ----------
function AdvocateRecap({ navigate }) {
  const [html, setHtml] = useState("");
  useEffect(() => {
    axios.get(`${API}/advocate/my-recap-preview`).then((r) => setHtml(r.data?.html || "")).catch(() => setHtml(""));
  }, []);
  return (
    <AppFrame>
      <TopBar title="My Friday Recap" onBack={() => navigate("/m/advocate")} />
      {!html ? <Skeleton /> : <iframe title="Recap" srcDoc={html} sandbox="" style={{ width: "100%", height: "70vh", border: `1px solid ${Palette.border}`, borderRadius: 14, background: "#fff" }} data-testid="mobile-recap-iframe" />}
    </AppFrame>
  );
}

// ---------- Member first screen: photo + code + menu ----------
function MemberHome({ navigate }) {
  const member = JSON.parse(localStorage.getItem("hrcpc_member") || "{}");
  const [referral, setReferral] = useState(null);
  useEffect(() => { axios.get(`${API}/client/referral`).then((r) => setReferral(r.data)).catch(() => setReferral(null)); }, []);
  const logout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem(LS_CLIENT);
    localStorage.removeItem("hrcpc_role");
    localStorage.removeItem("hrcpc_member");
    navigate("/m/");
  };
  const photo = member.photo_data_url || null;
  return (
    <AppFrame>
      <div style={{ padding: "18px 4px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>HRCPC Legal Diary</div>
        <GhostButton onClick={logout} data-testid="mobile-logout" style={{ background: "rgba(255,255,255,.08)", color: "#fff", borderColor: "rgba(255,255,255,.15)" }}>Logout</GhostButton>
      </div>

      <InstallBanner />
      <NotifButton />

      <Card style={{ padding: 0, overflow: "hidden", marginBottom: 16 }} data-testid="member-identity-card">
        <div style={{ background: `linear-gradient(135deg, ${Palette.navy}, #241f5a)`, color: "#fff", padding: "14px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", opacity: 0.75 }}>HRCPC Trust · Member</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>Your HRCPC Legal Diary</div>
        </div>
        <div style={{ padding: 20, textAlign: "center", background: "#fffef5" }}>
          {photo ? (
            <img src={photo} alt="Member" style={{ width: 128, height: 128, borderRadius: "50%", objectFit: "cover", border: `4px solid ${Palette.saffron}` }} data-testid="member-photo" />
          ) : (
            <div style={{ width: 128, height: 128, borderRadius: "50%", background: "#fef3c7", border: `4px solid ${Palette.saffron}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 42, color: "#92400e", fontWeight: 800 }}>
              {(member.full_name || "M").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ fontSize: 20, fontWeight: 800, color: Palette.ink, marginTop: 12 }}>{member.full_name || "Member"}</div>
          <div style={{ fontSize: 12, color: "#64748b", fontFamily: "ui-monospace,monospace", marginTop: 4 }}>{member.unique_code || ""}</div>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <MenuTile label="My Cases" sub="Status & updates" onClick={() => navigate("/m/member/cases")} colour={Palette.emerald} testid="tile-mcases" />
        <MenuTile label="Refer" sub="Family / neighbour" onClick={() => navigate("/m/member/refer")} colour={Palette.saffron} testid="tile-refer" />
        <MenuTile label="Pay Contribution" sub="UPI / Card" onClick={() => (window.location.href = "/support-pay")} colour="#0891b2" testid="tile-pay" />
        <MenuTile label="Help / Contact" sub="Reach HRCPC" onClick={() => (window.location.href = "mailto:support@hrcpc.org")} colour="#7c3aed" testid="tile-help" />
      </div>

      {referral && (
        <div style={{ marginTop: 12, fontSize: 12, color: "rgba(255,255,255,.9)", textAlign: "center" }}>
          Referrals · Pending: <b>{referral.counts?.pending || 0}</b> · Approved: <b>{referral.counts?.approved || 0}</b>
        </div>
      )}
    </AppFrame>
  );
}

// ---------- Member: My Cases ----------
function MemberCases({ navigate }) {
  const member = JSON.parse(localStorage.getItem("hrcpc_member") || "{}");
  const [cases, setCases] = useState(null);
  useEffect(() => { axios.get(`${API}/cases/client/${member.mobile}`).then((r) => setCases(r.data || [])).catch(() => setCases([])); }, [member.mobile]);
  const list = sortByDateAsc(cases || [], "next_hearing_date");
  return (
    <AppFrame>
      <TopBar title="My Cases" onBack={() => navigate("/m/member")} />
      {!cases ? <Skeleton /> : cases.length === 0 ? (
        <Card><em style={{ color: "#64748b" }}>No cases linked to your profile yet. Once HRCPC opens a case for you, it will appear here.</em></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.map((c) => (
            <Card key={c.case_number} onClick={() => navigate(`/m/member/cases/${encodeURIComponent(c.case_number)}`)} style={{ cursor: "pointer" }} data-testid={`member-case-${c.case_number}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800 }}>{c.title || "—"}</div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{c.case_number} · {c.court || c.forum_type}</div>
                </div>
                <StatusChip status={c.status} />
              </div>
              {c.next_hearing_date && <div style={{ marginTop: 10, fontSize: 12, background: "#fef3c7", color: "#92400e", padding: "6px 10px", borderRadius: 8, fontWeight: 700, display: "inline-block" }}>📅 Next: {fmtDate(c.next_hearing_date)}</div>}
            </Card>
          ))}
        </div>
      )}
    </AppFrame>
  );
}

// ---------- Member: Case Detail (updates + shared documents) ----------
function MemberCaseDetail({ navigate, caseNumber }) {
  const [updates, setUpdates] = useState(null);
  const [caseDoc, setCaseDoc] = useState(null);
  useEffect(() => {
    axios.get(`${API}/client/case-updates?case_number=${encodeURIComponent(caseNumber)}`).then((r) => setUpdates(r.data || [])).catch(() => setUpdates([]));
    axios.get(`${API}/cases/${encodeURIComponent(caseNumber)}`).then((r) => setCaseDoc(r.data || null)).catch(() => setCaseDoc(null));
  }, [caseNumber]);

  const openDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/case-document-content?case_id=${encodeURIComponent(caseNumber)}&doc_id=${encodeURIComponent(doc.id)}`);
      const dataUrl = r.data?.document?.data_url;
      if (!dataUrl) { toast.error("Document could not be loaded"); return; }
      const win = window.open("", "_blank");
      if (!win) { toast.error("Please allow pop-ups to view this document"); return; }
      // Escape user-supplied filename before splicing into HTML to prevent XSS.
      const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      const name = esc(doc.name || "Document");
      const isImg = dataUrl.startsWith("data:image/");
      const isPdf = dataUrl.startsWith("data:application/pdf");
      // Note: dataUrl is a base64 data: URL from our own API — safe in src/href.
      const body = isImg
        ? `<body style="margin:0;background:#0f172a;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${dataUrl}" style="max-width:100%;max-height:100vh"/></body>`
        : isPdf
        ? `<body style="margin:0"><embed src="${dataUrl}" type="application/pdf" width="100%" height="100%" style="height:100vh"/></body>`
        : `<body style="font-family:sans-serif;padding:24px"><h3>${name}</h3><p>This file type cannot be previewed. <a href="${dataUrl}" download="${name}">Tap to download</a></p></body>`;
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${name}</title></head>${body}</html>`;
      // Safe DOM write: navigate the popup to a Blob URL instead of using
      // document.write. Same visual result, no document.write API surface.
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (err) { /* noop */ } }, 5000);
    } catch (e) {
      console.error("[MemberCaseDetail.openDoc] failed:", e);
      toast.error(e?.response?.data?.detail || "Could not open document");
    }
  };

  const docs = (caseDoc?.documents || []).filter((d) => d && d.visible_to_client);

  return (
    <AppFrame>
      <TopBar title="Case Updates" onBack={() => navigate("/m/member/cases")} />
      <Card style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#64748b" }}>Case #</div>
        <div style={{ fontWeight: 800, fontFamily: "ui-monospace,monospace" }}>{caseNumber}</div>
        {caseDoc?.title && <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>{caseDoc.title}</div>}
        {caseDoc?.next_hearing_date && <div style={{ marginTop: 8, fontSize: 12, background: "#fef3c7", color: "#92400e", padding: "6px 10px", borderRadius: 8, fontWeight: 700, display: "inline-block" }}>📅 Next hearing: {fmtDate(caseDoc.next_hearing_date)}</div>}
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>Only updates & documents your advocate has explicitly shared with you are shown here.</div>
      </Card>

      <Section title={`Shared documents (${docs.length})`}>
        {docs.length === 0 ? (
          <Card data-testid="member-docs-empty"><em style={{ color: "#64748b" }}>No documents have been shared with you yet. Your advocate will share them here when needed.</em></Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {docs.map((d) => (
              <Card key={d.id} onClick={() => openDoc(d)} style={{ cursor: "pointer" }} data-testid={`member-doc-${d.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fef3c7", color: "#92400e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📄</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: Palette.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{d.type} · {fmtDate(d.date)}</div>
                  </div>
                  <div style={{ color: Palette.emerald, fontWeight: 800, fontSize: 13 }}>Open ›</div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Case updates (${(updates || []).length})`}>
        <List items={[...(updates || [])].sort((a,b) => (a.event_date || "") < (b.event_date || "") ? 1 : -1)} render={(u) => (
          <div>
            <div style={{ fontSize: 12, color: "#0f766e", fontWeight: 700 }}>{fmtDate(u.event_date)} · {u.event_type}</div>
            <div style={{ fontWeight: 700, marginTop: 4 }}>{u.details || u.summary || "—"}</div>
            {u.next_step && <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Next: {u.next_step} {u.next_step_due && `by ${fmtDate(u.next_step_due)}`}</div>}
          </div>
        )} emptyText="No case updates yet. Your advocate will publish one when there's progress worth sharing." />
      </Section>
    </AppFrame>
  );
}

// ---------- Member: Refer ----------
function MemberRefer({ navigate }) {
  const [ref, setRef] = useState(null);
  useEffect(() => { axios.get(`${API}/client/referral`).then((r) => setRef(r.data)).catch(() => setRef(null)); }, []);
  return (
    <AppFrame>
      <TopBar title="Refer a family / neighbour" onBack={() => navigate("/m/member")} />
      {!ref ? <Skeleton /> : (
        <Card>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: Palette.saffron, fontWeight: 700 }}>Your invite link</div>
          <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 12, padding: "10px 12px", background: "#f8fafc", border: `1px solid ${Palette.border}`, borderRadius: 10, marginTop: 6, wordBreak: "break-all" }} data-testid="mobile-referral-link">{ref.link}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <PrimaryButton onClick={() => { navigator.clipboard.writeText(ref.link); toast.success("Link copied"); }} style={{ flex: 1, width: "auto", padding: "12px 14px" }} data-testid="mobile-refer-copy">Copy link</PrimaryButton>
            <a href={ref.whatsapp_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, background: Palette.emerald, color: "#fff", padding: "12px 14px", borderRadius: 14, textAlign: "center", fontWeight: 700, textDecoration: "none" }} data-testid="mobile-refer-wa">WhatsApp</a>
          </div>
          <div style={{ marginTop: 14, fontSize: 13, color: "#475569", background: "#f8fafc", padding: 10, borderRadius: 10 }}>
            Referrals so far: <b>{ref.counts?.approved || 0}</b> approved · <b>{ref.counts?.pending || 0}</b> pending
          </div>
        </Card>
      )}
    </AppFrame>
  );
}

// ---------- Shared UI helpers ----------
const TopBar = ({ title, onBack }) => (
  <div style={{ display: "flex", alignItems: "center", padding: "18px 0 12px", gap: 8 }}>
    <button onClick={onBack} data-testid="mobile-back" style={{ background: "rgba(255,255,255,.15)", color: "#fff", border: "1px solid rgba(255,255,255,.2)", borderRadius: 12, padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>‹ Back</button>
    <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>{title}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ color: "#fff", fontSize: 12, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8, opacity: 0.85 }}>{title}</div>
    {children}
  </div>
);

const StatusChip = ({ status }) => {
  const s = (status || "").toLowerCase();
  const colours = {
    active: { bg: "#dbeafe", fg: "#1e40af" },
    pending: { bg: "#fef3c7", fg: "#92400e" },
    won: { bg: "#dcfce7", fg: "#065f46" },
    lost: { bg: "#fee2e2", fg: "#991b1b" },
    closed: { bg: "#f1f5f9", fg: "#475569" },
  };
  const c = colours[s] || colours.active;
  return <span style={{ background: c.bg, color: c.fg, fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>{status || "—"}</span>;
};

const List = ({ items, render, emptyText }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {items.length === 0 ? <Card><em style={{ color: "#64748b" }}>{emptyText}</em></Card> : items.map((it, i) => <Card key={it.id || it.case_number || i}>{render(it)}</Card>)}
  </div>
);

const Skeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {[0, 1, 2].map((i) => <div key={i} style={{ height: 76, background: "rgba(255,255,255,.6)", borderRadius: 16, animation: "pulse 1.5s ease-in-out infinite" }} />)}
  </div>
);

// ---------- Root router ----------
export default function MobileApp() {
  const [path, navigate] = useMobileRoute();

  // Register service worker for PWA install
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  // Route guards: bounce to /m/ if no token
  const role = localStorage.getItem("hrcpc_role");
  const hasAdmin = !!localStorage.getItem(LS_ADMIN);
  const hasClient = !!localStorage.getItem(LS_CLIENT);

  const isAdvocateRoute = path.startsWith("/m/advocate");
  const isMemberRoute = path.startsWith("/m/member");

  const renderScreen = () => {
    if (isAdvocateRoute && !hasAdmin) { window.history.replaceState({}, "", "/m/"); return <LoginScreen navigate={navigate} />; }
    if (isMemberRoute && !hasClient) { window.history.replaceState({}, "", "/m/"); return <LoginScreen navigate={navigate} />; }

    if (path === "/m/" || path === "/m") {
      if (hasAdmin && role === "advocate") { window.history.replaceState({}, "", "/m/advocate"); return <AdvocateHome navigate={navigate} />; }
      if (hasClient && role === "client") { window.history.replaceState({}, "", "/m/member"); return <MemberHome navigate={navigate} />; }
      return <LoginScreen navigate={navigate} />;
    }

    if (path === "/m/advocate") return <AdvocateHome navigate={navigate} />;
    if (path === "/m/advocate/home") return <AdvocateToday navigate={navigate} />;
    if (path === "/m/advocate/cases") return <AdvocateCases navigate={navigate} />;
    if (path === "/m/advocate/tasks") return <AdvocateTasks navigate={navigate} />;
    if (path === "/m/advocate/recap") return <AdvocateRecap navigate={navigate} />;
    const caseMatch = path.match(/^\/m\/advocate\/cases\/([^/]+)$/);
    if (caseMatch) return <AdvocateCaseDetail navigate={navigate} caseNumber={decodeURIComponent(caseMatch[1])} />;

    if (path === "/m/member") return <MemberHome navigate={navigate} />;
    if (path === "/m/member/cases") return <MemberCases navigate={navigate} />;
    if (path === "/m/member/refer") return <MemberRefer navigate={navigate} />;
    const mcaseMatch = path.match(/^\/m\/member\/cases\/([^/]+)$/);
    if (mcaseMatch) return <MemberCaseDetail navigate={navigate} caseNumber={decodeURIComponent(mcaseMatch[1])} />;

    return (
      <AppFrame>
        <Card style={{ marginTop: 24, textAlign: "center" }}>
          <div style={{ fontSize: 42 }}>🕉️</div>
          <div style={{ fontWeight: 800, marginTop: 8 }}>Page not found</div>
          <PrimaryButton onClick={() => navigate("/m/")} style={{ marginTop: 12 }}>Go to app home</PrimaryButton>
        </Card>
      </AppFrame>
    );
  };

  return (
    <>
      {renderScreen()}
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}
